import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@devmil/contracts';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';

/**
 * Costura entre o painel e o WebAuthn do navegador.
 *
 * A cerimônia real vive em `navigator.credentials`, atrás da
 * `@simplewebauthn/browser`. A costura existe para o teste injetar um dublê
 * NESTE nível: um dublê de `navigator.credentials` cru obrigaria o teste a
 * imitar `ArrayBuffer`, `getTransports()` e os detalhes internos da lib, e aí
 * o teste provaria a lib, não o painel. Nenhum aparelho real participa dos
 * testes.
 */
export interface WebAuthnClient {
  /** O painel decide mostrar ou desabilitar o botão por esta resposta. */
  supported(): boolean;
  register(
    options: PublicKeyCredentialCreationOptionsJSON,
  ): Promise<RegistrationResponseJSON>;
  authenticate(
    options: PublicKeyCredentialRequestOptionsJSON,
  ): Promise<AuthenticationResponseJSON>;
}

export const browserWebAuthnClient: WebAuthnClient = {
  supported: () => browserSupportsWebAuthn(),
  register: (options) => startRegistration({optionsJSON: options}),
  authenticate: (options) => startAuthentication({optionsJSON: options}),
};

/**
 * Cancelamento pelo usuário, ou pelo tempo esgotado do seletor.
 *
 * O navegador responde `NotAllowedError` para os dois, de propósito (a
 * especificação não deixa distinguir, para o site não descobrir o que existe
 * no autenticador). `AbortError` entra porque a lib aborta a cerimônia
 * anterior quando outra começa. Cancelar não é defeito: a tela volta ao
 * estado de antes do clique, com o botão vivo.
 */
export const cancelamentoDoUsuario = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'NotAllowedError' || error.name === 'AbortError');
